import PropTypes from 'prop-types';
import { combineReducers } from 'redux';
import { globalizeSelectors } from '../Utils';
import {
  createSingleDataReducer,
  singleDataType,
  clearResults,
  setData,
} from '../factories/ApiCall';
import type { Customer } from 'js/api/types/Order';
import type {
  CustomerAction,
  CustomerSearchResults,
  CustomerState,
  LoadCustomerPayload,
  SearchCustomersPayload,
} from '../types/Customer';

/*
 * action types
 */

const types = {
  SEARCH_CUSTOMERS: 'CUSTOMER/SEARCH_CUSTOMERS',
  LOAD_CUSTOMER: 'CUSTOMER/LOAD_CUSTOMER',
  SAVE_CUSTOMER: 'CUSTOMER/SAVE_CUSTOMER',
} as const;

const mountPath = 'customer'; // mount point in global state, must match root reducer

/*
 * action creators
 */

const actions = {
  searchCustomers: (
    search: string,
    page: number,
    only_local_customers: boolean | undefined = undefined
  ): CustomerAction<SearchCustomersPayload> => ({
    type: types.SEARCH_CUSTOMERS,
    payload: { search, page, only_local_customers },
  }),
  clearSearchCustomers: (): CustomerAction => ({
    type: clearResults(types.SEARCH_CUSTOMERS),
  }),
  loadCustomer: (customerid: number): CustomerAction<LoadCustomerPayload> => ({
    type: types.LOAD_CUSTOMER,
    payload: { customerid },
  }),
  selectCustomer: (
    customer: Customer | null
  ): CustomerAction<Customer | null> => ({
    type: setData(types.LOAD_CUSTOMER),
    payload: customer,
  }),
  saveCustomer: (customer: Customer): CustomerAction<Customer> => ({
    type: types.SAVE_CUSTOMER,
    payload: customer,
  }),
};

/*
 * state shape
 */
const propType = PropTypes.shape({
  search_results: singleDataType,
  selected_customer: singleDataType,
});

/*
 * reducer
 */
const reducer = combineReducers({
  search_results: createSingleDataReducer<
    CustomerSearchResults,
    SearchCustomersPayload
  >(types.SEARCH_CUSTOMERS, false),
  selected_customer: createSingleDataReducer<Customer, LoadCustomerPayload>(
    types.LOAD_CUSTOMER
  ),
});

/*
 * selectors
 */
const localSelectors = {
  search_results: (state: CustomerState): CustomerState['search_results'] =>
    state.search_results,
  selected_customer: (state: CustomerState): Customer | null =>
    state.selected_customer.data,
  selected_customer_pending: (state: CustomerState): boolean =>
    state.selected_customer.pending,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  propType,
  selectors,
  reducer,
};
